import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AntiCheat from '../../components/AntiCheat/AntiCheat';
import PreExamCheck from '../../components/PreExamCheck/PreExamCheck';
import { formatTime } from '../../utils/helpers';
import { 
  Container, Paper, Typography, Radio, RadioGroup, 
  FormControlLabel, Button, LinearProgress, Box, 
  CircularProgress, Divider, IconButton, Alert 
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import axios from '../../services/axios';

const AudioPlayer = ({ audioUrl, questionNumber }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasPlayed, setHasPlayed] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleEnded = () => {
      setIsPlaying(false);
      setHasPlayed(true);
    };

    const handleError = () => {
      setError(true);
    };

    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);

    return () => {
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
    };
  }, []);

  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (!audio || hasPlayed) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.play().catch(err => {
        console.error('Audio play error:', err);
        setError(true);
      });
      setIsPlaying(true);
    }
  };

  if (!audioUrl) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        Audio not available for this question.
      </Alert>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        ❌ Failed to load audio
      </Alert>
    );
  }

  return (
    <Box sx={{ 
      mb: 3, 
      p: 2, 
      bgcolor: '#f0f9ff', 
      borderRadius: 2,
      border: '2px solid #3b82f6'
    }}>
      <audio ref={audioRef} src={audioUrl} preload="metadata" />
      
      <Box display="flex" alignItems="center" gap={2}>
        <IconButton
          onClick={togglePlayPause}
          disabled={hasPlayed}
          sx={{
            bgcolor: hasPlayed ? '#d1d5db' : '#3b82f6',
            color: 'white',
            '&:hover': {
              bgcolor: hasPlayed ? '#d1d5db' : '#2563eb'
            }
          }}
        >
          {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
        </IconButton>

        <Box flex={1}>
          <Typography variant="body2" fontWeight="bold" color="#1e40af">
            🎧 Audio Question {questionNumber} {hasPlayed && "(Already Played)"}
          </Typography>
          
          {hasPlayed && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
              ⚠️ Audio has been played 1x
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const ListeningInstructions = ({ partType, questionRange }) => {
  const instructions = {
    partA: {
      title: "Part A",
      text: "In Part A you will hear short conversations between two people. After each conversation, you will hear a question about the conversation. The conversations and questions will not be repeated. After you hear a question, read the four possible answers in your test book and choose the best answer. Then on your answer sheet, find the number of the question and fill in the space that corresponds to the letter of the answer you have chosen."
    },
    partB: {
      title: "Part B",
      text: "In this part of the test, you will hear longer conversations. After each conversation, you will hear several questions. The conversations and questions will not be repeated. After you hear a question, read the four possible answers in your test book and choose the best answer. Then, on your answer sheet, find the number of the question and fill in the space that corresponds to the letter of the answer you have chosen."
    },
    partC: {
      title: "Part C",
      text: "In this part of the test, you will hear several talks. After each talk, you will hear some questions. The talks and questions will not be repeated. After you hear a question, read the four possible answers in your test book and choose the best answer. Then, on your answer sheet, find the number of the question and fill in the space that corresponds to the letter of the answer you have chosen."
    }
  };

  const content = instructions[partType];
  if (!content) return null;

  return (
    <Box sx={{ 
      mb: 4, 
      p: 3, 
      bgcolor: '#f8f9fa',
      border: '2px solid #1a3c6d',
      borderRadius: 2
    }}>
      <Typography variant="h6" fontWeight="bold" color="#1a3c6d" gutterBottom>
        {content.title} {questionRange && `(Questions ${questionRange})`}
      </Typography>
      <Typography variant="body2" sx={{ 
        fontStyle: 'italic',
        lineHeight: 1.8,
        color: '#495057'
      }}>
        <strong>Directions:</strong> {content.text}
      </Typography>
    </Box>
  );
};

const AudioListening = () => {
  const navigate = useNavigate();
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(30 * 60);
  const [examStarted, setExamStarted] = useState(false);
  const [showPreCheck, setShowPreCheck] = useState(false);
  const [violationCount, setViolationCount] = useState(0);
  const [hasOtherTabs, setHasOtherTabs] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [backendResponse, setBackendResponse] = useState(null);
  const [unansweredQuestions, setUnansweredQuestions] = useState([]);
  const timerRef = useRef(null);
  const hasSubmittedRef = useRef(false);
  const hasFetchedRef = useRef(false);
  const questionRefs = useRef({});

  const getPartType = (index) => {
    const totalQuestions = questions.length;
    const partAEnd = Math.floor(totalQuestions * 0.4);
    const partBEnd = Math.floor(totalQuestions * 0.7);
    
    if (index < partAEnd) return 'partA';
    if (index < partBEnd) return 'partB';
    return 'partC';
  };

  const shouldShowInstruction = (index) => {
    if (index === 0) return 'partA';
    
    const currentPart = getPartType(index);
    const previousPart = getPartType(index - 1);
    
    if (currentPart !== previousPart) {
      return currentPart;
    }
    
    return null;
  };

  const getQuestionRange = (partType) => {
    const totalQuestions = questions.length;
    const partAEnd = Math.floor(totalQuestions * 0.4);
    const partBEnd = Math.floor(totalQuestions * 0.7);
    
    if (partType === 'partA') return `1-${partAEnd}`;
    if (partType === 'partB') return `${partAEnd + 1}-${partBEnd}`;
    return `${partBEnd + 1}-${totalQuestions}`;
  };

  useEffect(() => {
    const listeningCompleted = localStorage.getItem('listening_completed');
    if (listeningCompleted === 'true') {
      alert('You have already completed the Listening section!');
      navigate('/exam/structure', { replace: true });
      return;
    }

    const savedTime = localStorage.getItem('listening_time');
    if (savedTime && parseInt(savedTime) > 0) {
      setTimeLeft(parseInt(savedTime));
    }

    const savedAnswers = localStorage.getItem('listening_answers');
    if (savedAnswers) {
      try {
        setAnswers(JSON.parse(savedAnswers));
      } catch (err) {
        console.error('Error parsing saved answers:', err);
      }
    }
  }, [navigate]);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    let retryCount = 0;
    const maxRetries = 3;

    const fetchQuestions = async () => {
      try {
        setLoading(true);
        setFetchError(false);
        
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        
        const res = await axios.get('/exam/questions/listening', { headers });
        
        setBackendResponse(res.data);

const existingSessionId = localStorage.getItem('exam_session_id');
let newSessionId = res.data.session_id || res.data.session?.id || res.data?.data?.session_id;

// ✅ FORCE CREATE SESSION ID jika tidak ada
if (!newSessionId && !existingSessionId) {
  newSessionId = Date.now(); // Gunakan timestamp sebagai session ID
  console.log('⚠️ Backend tidak kirim session_id, generate temporary:', newSessionId);
}

if (!existingSessionId && newSessionId) {
  localStorage.setItem('exam_session_id', newSessionId.toString());
  console.log('✅ Session ID saved:', newSessionId);
} else if (existingSessionId) {
  console.log('✅ Session ID preserved:', existingSessionId);
}
        let loadedQuestions = [];
        
        if (res.data && Array.isArray(res.data)) {
          loadedQuestions = res.data;
        } else if (res.data && res.data.questions && Array.isArray(res.data.questions)) {
          loadedQuestions = res.data.questions;
        } else if (res.data && res.data.data && Array.isArray(res.data.data)) {
          loadedQuestions = res.data.data;
        } else if (res.data && res.data.listening && Array.isArray(res.data.listening)) {
          loadedQuestions = res.data.listening;
        } else if (res.data && res.data.listening_questions && Array.isArray(res.data.listening_questions)) {
          loadedQuestions = res.data.listening_questions;
        } else {
          for (const key in res.data) {
            if (Array.isArray(res.data[key])) {
              loadedQuestions = res.data[key];
              break;
            }
          }
        }
        
        const normalizedQuestions = loadedQuestions.map((q, idx) => {
          let questionText = 'Question not available';
          if (typeof q === 'string') {
            questionText = q;
          } else if (q && typeof q === 'object') {
            questionText = q.question_text || q.question || q.text || q.sentence || 
                          q.description || q.prompt || q.content || `Listening Question ${idx + 1}`;
          }
          
          const audioUrl = q?.audio_url || q?.audioUrl || q?.audio || q?.audio_file || 
                          q?.audio_link || q?.sound_url || null;
          
          const imageUrl = q?.image_url || q?.imageUrl || q?.image || q?.image_file || 
                          q?.image_link || q?.picture_url || null;
          
          let options = [];
          if (q?.options && Array.isArray(q.options)) {
            options = q.options.map((opt, optIdx) => {
              if (typeof opt === 'string') {
                return {
                  value: String.fromCharCode(65 + optIdx),
                  text: opt
                };
              } else if (typeof opt === 'object') {
                return {
                  value: opt.value || opt.id || opt.letter || String.fromCharCode(65 + optIdx),
                  text: opt.text || opt.label || opt.option || opt.content || `Option ${optIdx + 1}`
                };
              }
              return {
                value: String.fromCharCode(65 + optIdx),
                text: `Option ${optIdx + 1}`
              };
            });
          } else if (q?.choices && Array.isArray(q.choices)) {
            options = q.choices.map((choice, optIdx) => ({
              value: String.fromCharCode(65 + optIdx),
              text: choice
            }));
          } else {
            options = ['A', 'B', 'C', 'D'].map((letter, optIdx) => ({
              value: letter,
              text: `Option ${optIdx + 1}`
            }));
          }
          
          return {
            id: q?.id || q?.question_id || `listening_${idx + 1}`,
            question: questionText,
            audio_url: audioUrl,
            image_url: imageUrl,
            options: options,
            raw: q
          };
        });
        
        if (normalizedQuestions.length > 0) {
          setQuestions(normalizedQuestions);
          setLoading(false);
        } else {
          if (retryCount < maxRetries) {
            retryCount++;
            setTimeout(fetchQuestions, 2000);
          } else {
            setFetchError(true);
            setLoading(false);
          }
        }
        
      } catch (err) {
        if (retryCount < maxRetries && err.response?.status !== 401) {
          retryCount++;
          setTimeout(fetchQuestions, 3000);
        } else {
          setFetchError(true);
          setLoading(false);
          
          if (err.response?.status === 401) {
            alert('Session expired. Please login again.');
            navigate('/login');
          }
        }
      }
    };

    fetchQuestions();
  }, [navigate]);

  const handlePreCheckReady = async () => {
    setShowPreCheck(false);
    const elem = document.documentElement;
    try {
      if (elem.requestFullscreen) {
        await elem.requestFullscreen();
      } else if (elem.webkitRequestFullscreen) {
        await elem.webkitRequestFullscreen();
      } else if (elem.msRequestFullscreen) {
        await elem.msRequestFullscreen();
      }
      setExamStarted(true);
    } catch (err) {
      console.error('Fullscreen request failed:', err);
      setExamStarted(true);
    }
  };

  const handlePreCheckFail = () => {
    setShowPreCheck(false);
    navigate('/dashboard', { replace: true });
  };

  const startExam = () => {
    if (questions.length === 0) {
      alert('Cannot start exam: Questions not available. Please refresh the page or contact admin.');
      return;
    }
    setShowPreCheck(true);
  };

  const handleSubmit = () => {
    if (hasSubmittedRef.current) return;
    hasSubmittedRef.current = true;
    
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    localStorage.setItem('listening_answers', JSON.stringify(answers));
    localStorage.setItem('listening_time', timeLeft.toString());
    localStorage.setItem('listening_completed', 'true');
    
    setTimeout(() => {
      navigate('/exam/structure', { replace: true });
    }, 300);
  };

  useEffect(() => {
    window.autoSubmitExam = handleSubmit;
    return () => {
      delete window.autoSubmitExam;
    };
  }, [answers, timeLeft]);

  useEffect(() => {
    if (!examStarted) return;

    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          if (window.autoSubmitExam) {
            window.autoSubmitExam();
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [examStarted]);

  const handleAnswer = (questionIndex, value) => {
    setAnswers({ ...answers, [questionIndex]: value });
    setUnansweredQuestions(prev => prev.filter(q => q !== questionIndex));
  };

  const handleFinish = () => {
    const unanswered = [];
    questions.forEach((_, index) => {
      if (!answers[index]) {
        unanswered.push(index);
      }
    });

    if (unanswered.length > 0) {
      setUnansweredQuestions(unanswered);
      const firstUnanswered = unanswered[0];
      if (questionRefs.current[firstUnanswered]) {
        questionRefs.current[firstUnanswered].scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
      }
      return;
    }
    
    handleSubmit();
  };

  const handleViolation = (count) => {
    setViolationCount(count);
    if (count >= 3) {
      alert('You have violated 3 times. Exam will be auto-submitted.');
      handleSubmit();
    }
  };

  const handleTabDetection = (detected) => {
    setHasOtherTabs(detected);
  };

  const answeredCount = Object.keys(answers).length;
  const progress = questions.length > 0 ? (answeredCount / questions.length) * 100 : 0;

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ mt: 12, textAlign: 'center' }}>
        <Paper elevation={8} sx={{ p: 6, borderRadius: 3 }}>
          <CircularProgress size={60} sx={{ mb: 3 }} />
          <Typography variant="h5" color="#1a3c6d" gutterBottom>
            Loading Listening Questions...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please wait a moment
          </Typography>
        </Paper>
      </Container>
    );
  }

  if (fetchError || questions.length === 0) {
    return (
      <Container maxWidth="sm" sx={{ mt: 12, textAlign: 'center' }}>
        <Paper elevation={8} sx={{ p: 6, borderRadius: 3 }}>
          <Typography variant="h5" color="error" gutterBottom>
            ❌ Failed to Load Questions
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Unable to fetch listening questions from server.
          </Typography>
          
          {backendResponse && (
            <Box sx={{ bgcolor: '#fff3cd', p: 2, borderRadius: 2, mb: 3, textAlign: 'left' }}>
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                🛠 Debug Information:
              </Typography>
              <Typography variant="caption" component="div">
                <pre style={{ fontSize: '10px', overflow: 'auto', maxHeight: '200px' }}>
                  {JSON.stringify(backendResponse, null, 2)}
                </pre>
              </Typography>
            </Box>
          )}
          
          <Box sx={{ bgcolor: '#fff3cd', p: 2, borderRadius: 2, mb: 3 }}>
            <Typography variant="body2" color="text.primary" textAlign="left">
              Possible causes:<br />
              • Data format from backend does not match<br />
              • Connection to server interrupted<br />
              • Questions not yet available in database<br />
              • Login session expired
            </Typography>
          </Box>
          <Button
            variant="contained"
            onClick={() => window.location.reload()}
            sx={{ bgcolor: '#1a3c6d', mr: 2, mb: 2 }}
          >
            🔄 Refresh Page
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate('/dashboard')}
          >
            Back to Dashboard
          </Button>
        </Paper>
      </Container>
    );
  }

  if (showPreCheck) {
    return (
      <PreExamCheck 
        onReady={handlePreCheckReady}
        onFail={handlePreCheckFail}
      />
    );
  }

  if (!examStarted) {
    return (
      <Container maxWidth="sm" sx={{ mt: 12, textAlign: 'center' }}>
        <Paper elevation={8} sx={{ p: 6, borderRadius: 3 }}>
          <Typography variant="h4" fontWeight="bold" color="#1a3c6d" gutterBottom>
            Listening Comprehension
          </Typography>
          <Typography variant="h6" color="text.secondary" paragraph>
            {questions.length} questions • 30 minutes
          </Typography>
          
          <Alert severity="success" sx={{ mb: 2 }}>
            ✅ Questions successfully loaded ({questions.length} questions)
          </Alert>
          
          <Box sx={{ bgcolor: '#fff3cd', p: 2, borderRadius: 2, mb: 3 }}>
            <Typography variant="body2" color="text.primary" fontWeight="bold" gutterBottom>
              ⚠️ STRICT RULES
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="left">
              • Exam will run in fullscreen mode<br />
              • Audio will play ONCE ONLY<br />
              • Do not open other tabs/applications<br />
              • Do not use keyboard shortcuts<br />
              • 3x violations = auto submit<br />
              • All questions displayed on 1 page
            </Typography>
          </Box>

          <Typography variant="body2" color="error" sx={{ mb: 3, fontWeight: 'bold' }}>
            🎧 Make sure your headphones/speakers are ready!
          </Typography>

          <Button
            variant="contained"
            size="large"
            onClick={startExam}
            sx={{ bgcolor: '#1a3c6d', px: 6, py: 2, fontSize: 18 }}
          >
            Start Pre-Check
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <>
      <AntiCheat 
        onViolation={handleViolation}
        onTabDetected={handleTabDetection}
      />
      
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper 
          elevation={3} 
          sx={{ 
            p: 3, 
            mb: 3, 
            position: 'sticky', 
            top: 0, 
            zIndex: 1000,
            bgcolor: 'white'
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h5" fontWeight="bold">Listening Comprehension</Typography>
              <Typography variant="body2" color="text.secondary">
                Answered: {answeredCount}/{questions.length} questions
              </Typography>
            </Box>
            <Box textAlign="right">
              <Typography variant="h6">
                Time: <strong style={{ color: timeLeft < 300 ? '#e74c3c' : '#1a3c6d' }}>
                  {formatTime(timeLeft)}
                </strong>
              </Typography>
              {violationCount > 0 && (
                <Typography variant="caption" color="error" display="block">
                  ⚠️ Violations: {violationCount}/3
                </Typography>
              )}
              {hasOtherTabs && (
                <Typography variant="caption" sx={{ color: '#ff9800' }} display="block">
                  ⚠️ Other tabs detected!
                </Typography>
              )}
            </Box>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={progress} 
            sx={{ mt: 2, height: 8, borderRadius: 5 }} 
          />
        </Paper>

        {unansweredQuestions.length > 0 && (
          <Alert 
            severity="warning" 
            sx={{ mb: 3 }}
            icon={<ErrorOutlineIcon />}
          >
            <Typography variant="body2" fontWeight="bold">
              You have {unansweredQuestions.length} unanswered question(s)
            </Typography>
            <Typography variant="caption">
              Scroll down to find questions marked with red indicator
            </Typography>
          </Alert>
        )}

        <Paper elevation={3} sx={{ p: 4, userSelect: 'none' }}>
          {questions.map((q, index) => {
            const instructionType = shouldShowInstruction(index);
            const isUnanswered = unansweredQuestions.includes(index);
            
            return (
              <Box 
                key={q.id || index} 
                sx={{ mb: 4 }}
                ref={el => questionRefs.current[index] = el}
              >
                {instructionType && (
                  <ListeningInstructions 
                    partType={instructionType} 
                    questionRange={getQuestionRange(instructionType)}
                  />
                )}
                
                <Box sx={{
                  p: 2,
                  borderRadius: 2,
                  border: isUnanswered ? '2px solid #ef4444' : '2px solid transparent',
                  bgcolor: isUnanswered ? '#fef2f2' : 'transparent',
                  transition: 'all 0.3s ease'
                }}>
                  <Typography variant="h6" gutterBottom sx={{ 
                    color: isUnanswered ? '#dc2626' : '#1a3c6d',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}>
                    {isUnanswered && <ErrorOutlineIcon sx={{ color: '#dc2626' }} />}
                    Question {index + 1}
                    {answers[index] && (
                      <Typography 
                        component="span" 
                        variant="caption" 
                        sx={{ ml: 2, color: '#2e7d32', fontWeight: 'bold' }}
                      >
                        ✓ Answered
                      </Typography>
                    )}
                    {isUnanswered && (
                      <Typography 
                        component="span" 
                        variant="caption" 
                        sx={{ ml: 2, color: '#dc2626', fontWeight: 'bold' }}
                      >
                        ⚠ Not Answered
                      </Typography>
                    )}
                  </Typography>
                  
                  <AudioPlayer 
                    audioUrl={q.audio_url} 
                    questionNumber={index + 1} 
                  />
                  
                  {q.image_url && (
                    <Box sx={{ mb: 3, textAlign: 'center' }}>
                      <img 
                        src={q.image_url} 
                        alt={`Question ${index + 1}`}
                        style={{ 
                          maxWidth: '100%', 
                          maxHeight: '400px', 
                          borderRadius: '8px',
                          border: '2px solid #e5e7eb'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </Box>
                  )}

                  <Typography paragraph sx={{ mb: 3, fontSize: '15px' }}>
                    {q.question}
                  </Typography>

                  <RadioGroup 
                    value={answers[index] || ''} 
                    onChange={(e) => handleAnswer(index, e.target.value)}
                  >
                    {q.options.map((opt, i) => (
                      <FormControlLabel 
                        key={opt.value || i}
                        value={opt.value} 
                        control={<Radio />} 
                        label={
                          <Typography>
                            <strong>{opt.value}.</strong> {opt.text}
                          </Typography>
                        }
                        sx={{ 
                          mb: 1, 
                          cursor: 'pointer !important',
                          '&:hover': {
                            bgcolor: '#f5f5f5',
                            borderRadius: 1
                          },
                          p: 1,
                          border: '1px solid transparent',
                          '&.Mui-checked': {
                            border: '1px solid #3b82f6',
                            borderRadius: 1,
                            bgcolor: '#e0f2fe'
                          }
                        }} 
                      />
                    ))}
                  </RadioGroup>
                </Box>

                {index < questions.length - 1 && (
                  <Divider sx={{ mt: 4 }} />
                )}
              </Box>
            );
          })}

          <Box sx={{ mt: 5, textAlign: 'center' }}>
            <Button 
              variant="contained" 
              size="large"
              onClick={handleFinish}
              sx={{ 
                bgcolor: '#1a3c6d', 
                px: 8, 
                py: 2,
                fontSize: 18,
                cursor: 'pointer !important' 
              }}
            >
              Continue to Structure →
            </Button>
          </Box>
        </Paper>
      </Container>
    </>
  );
};

export default AudioListening;
